import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { 
  FaSearch, FaDownload, FaFilePdf, FaFileCsv, FaFilter, 
  FaClock, FaCheckCircle, FaExclamationCircle,
  FaCalendarAlt, FaUser, FaTag, FaRedo, FaEye
} from "react-icons/fa";

import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import fetchData from "../../functions/fetchData";
import { fetchComboData } from "../../services/api";

export default function SaleLedger() {
  const [data, setData] = useState([]);
  const [Alldata, setAllData] = useState([]);
  const [customerdata, setCustomerdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [Totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formdata, setFormData] = useState({
    from: "",
    to: "",
    accounttype: "",
    customername: "",
    account_id: "",
    status: "all"
  });

  const quickFilters = [
    { key: 'all', label: 'All Records', icon: FaEye, color: 'primary' },
    { key: 'pending', label: 'Pending', icon: FaClock, color: 'warning' },
    { key: 'completed', label: 'Completed', icon: FaCheckCircle, color: 'success' },
    { key: 'overdue', label: 'Overdue', icon: FaExclamationCircle, color: 'danger' }
  ];

  const accountTypes = [
    { value: '', label: 'All Account Types' },
    { value: 'Sales', label: '💰 Sales' },
    { value: 'Cash', label: '💵 Cash' },
    { value: 'Discount', label: '🏷️ Discount' },
    { value: 'Account Recievable', label: '📋 Account Receivable' },
    { value: 'Purchase', label: '🛒 Purchase' },
    { value: 'Expense', label: '💸 Expense' }
  ];

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      accounttype: "",
      customername: "",
      account_id: "",
      status: "all"
    });
    setQuickFilter('all');
    setSearchTerm('');
    setData(Alldata);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let currentY = 22;
    
    doc.setFontSize(18);
    doc.text('Customer Ledger Report', 14, currentY);
    currentY += 8;
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, 14, currentY);
    currentY += 6;
    
    // Add customer name if selected
    if (formdata.customername) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Customer: ${formdata.customername}`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 6;
    }
    
    // Add date range if selected
    if (formdata.from || formdata.to) {
      doc.setFontSize(10);
      const dateRange = `Period: ${formdata.from ? formatDisplayDate(formdata.from) : 'Start'} to ${formdata.to ? formatDisplayDate(formdata.to) : 'End'}`;
      doc.text(dateRange, 14, currentY);
      currentY += 6;
    }
    
    const tableColumn = ["Txn ID", "Date", "A/C Type", "Description", "Debit", "Credit", "Balance"];
    const tableRows = [];

    let runningBalance = 0;
    data.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      runningBalance += debit - credit;
      
      tableRows.push([
        row.transaction_id,
        formatDisplayDate(row.date),
        row.account_type,
        row.description,
        debit.toFixed(2),
        credit.toFixed(2),
        runningBalance.toFixed(2)
      ]);
    });

    tableRows.push([
      "TOTALS",
      "",
      "",
      "",
      Totals.debit.toFixed(2),
      Totals.credit.toFixed(2),
      Totals.balance.toFixed(2)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 2,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      didParseCell: function(data) {
        // Check if this is the totals row (last row)
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255]; // Soft blue background (Alice Blue)
          data.cell.styles.textColor = [0, 0, 0]; // Black text
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10; // Larger font size
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [100, 149, 237]; // Cornflower blue border
        }
      }
    });

    doc.save(`ledger_report_${fmt(new Date(), "ddMMyyyy")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportCSVData = [
    // Add header information rows
    ...(formdata.customername ? [
      { 
        transaction_id: 'Customer Ledger Report',
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Customer: ${formdata.customername}`,
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ] : [
      { 
        transaction_id: 'Customer Ledger Report',
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ]),
    // Add data rows
    ...data.map((row, index) => {
      let runningBalance = 0;
      data.slice(0, index + 1).forEach(r => {
        runningBalance += (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
      });
      
      return {
        transaction_id: row.transaction_id,
        date: formatDisplayDate(row.date),
        customer_name: row.customer_name,
        account_type: row.account_type,
        account_id: row.account_id,
        description: row.description,
        debit_amount: row.debit_amount,
        credit_amount: row.credit_amount,
        running_balance: runningBalance.toFixed(2)
      };
    })
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const fetched = await fetchData("ledger_entries", null, "id", {});
        setAllData(fetched);
        setData(fetched);
        
        const result = await fetchComboData("customers", "name");
        setCustomerdata(result);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const filterData = () => {
      const { from, to, accounttype, account_id } = formdata;
      let filtered = [...Alldata];

      console.log('Total records before filter:', filtered.length);
      console.log('Filter params:', { from, to, accounttype, account_id });

      // Apply search term filter
      if (searchTerm) {
        filtered = filtered.filter(r => 
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_id?.toString().includes(searchTerm)
        );
        console.log('After search filter:', filtered.length);
      }

      // Date range filter
      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
        console.log('After date filter:', filtered.length);
      }

      // Account type filter - ONLY filter if accounttype is explicitly set
      if (accounttype && accounttype !== '') {
        filtered = filtered.filter((r) => r.account_type === accounttype);
        console.log('After account type filter:', filtered.length, 'Type:', accounttype);
      }

      // Customer filter - show ALL account types for this customer
      if (account_id) {
        filtered = filtered.filter((r) => r.account_id === parseInt(account_id));
        console.log('After customer filter:', filtered.length, 'Customer ID:', account_id);
        console.log('Account types in filtered data:', [...new Set(filtered.map(r => r.account_type))]);
      }

      // Quick filter for status
      if (quickFilter !== 'all') {
        const today = new Date();
        filtered = filtered.filter(r => {
          const balance = (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
          switch (quickFilter) {
            case 'pending':
              return balance > 0;
            case 'completed':
              return balance <= 0;
            case 'overdue':
              const recordDate = parseISO(r.date);
              const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
              return balance > 0 && daysDiff > 30;
            default:
              return true;
          }
        });
        console.log('After quick filter:', filtered.length);
      }

      const credit = filtered.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
      const debit = filtered.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);
      console.log('Totals - Debit:', debit, 'Credit:', credit, 'Balance:', debit - credit);
      
      // Show warning if only Sales records found (backend data issue)
      if (account_id && filtered.length > 0) {
        const accountTypes = [...new Set(filtered.map(r => r.account_type))];
        if (accountTypes.length === 1 && accountTypes[0] === 'Sales' && debit === 0) {
          console.warn('⚠️ WARNING: Only Sales entries found with no Debit amounts.');
          console.warn('This indicates a backend data issue. Sales should be recorded as DEBIT (customer owes) not CREDIT.');
          console.warn('Please check that ledger_entries are being created correctly for:');
          console.warn('- Sales/Invoices → DEBIT (customer owes)');
          console.warn('- Payments received → CREDIT (customer paid)');
          console.warn('- Discounts given → CREDIT (reducing amount owed)');
        }
      }
      
      setTotals({ credit, debit, balance: debit - credit });

      setData(filtered);
    };

    filterData();
  }, [formdata, Alldata, quickFilter, searchTerm]);

  const getStatusBadge = (record) => {
    const balance = (Number(record.debit_amount) || 0) - (Number(record.credit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
    
    if (balance <= 0) {
      return <span className="badge bg-success">Completed</span>;
    } else if (daysDiff > 30) {
      return <span className="badge bg-danger">Overdue</span>;
    } else {
      return <span className="badge bg-warning text-dark">Pending</span>;
    }
  };

  return (
    <Layout>
      <Header title="📊 Customer Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid p-4">
        {/* Summary Cards - Moved to Top */}
        <div className="row">
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Credit</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{Totals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
                <div className="position-absolute top-0 end-0 m-2">
                  <div className="bg-white bg-opacity-10 rounded-circle" style={{ width: '40px', height: '40px' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)', 
              borderRadius: '12px',
              minHeight: '80px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Debit</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{Totals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
                <div className="position-absolute top-0 end-0 m-2">
                  <div className="bg-white bg-opacity-10 rounded-circle" style={{ width: '40px', height: '40px' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: Totals.balance >= 0 ? 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)' : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Net Balance</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{Totals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaDownload size={20} className="text-white" />
                  </div>
                </div>
                <div className="position-absolute top-0 end-0 m-2">
                  <div className="bg-white bg-opacity-10 rounded-circle" style={{ width: '40px', height: '40px' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-lg" style={{ 
              background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)', 
              borderRadius: '12px',
              minHeight: '120px'
            }}>
              <div className="card-body p-3 text-white position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Records</h6>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{data.length.toLocaleString('th-TH')}</h4>
                  </div>
                  <div className="bg-white bg-opacity-25 rounded-circle p-2">
                    <FaEye size={20} className="text-white" />
                  </div>
                </div>
                <div className="position-absolute top-0 end-0 m-2">
                  <div className="bg-white bg-opacity-10 rounded-circle" style={{ width: '40px', height: '40px' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Header with Quick Actions */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">Customer Ledger</h5>
                    <span className="badge text-white fs-6" style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #007bff 100%)' }}>{data.length} Records</span>
                  </div>
                </div>

                {/* Search Bar with All Action Buttons */}
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="input-group">
                      <span className="input-group-text" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                        <FaSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Transaction ID, Description, Customer..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ borderLeft: 'none' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-8 d-flex justify-content-end align-items-center gap-2 flex-wrap">
                    {/* Quick Filter Buttons */}
                    {quickFilters.map(filter => {
                      const Icon = filter.icon;
                      const gradients = {
                        primary: 'linear-gradient(135deg, #4e73df 0%, #224abe 100%)',
                        warning: 'linear-gradient(135deg, #f6c23e 0%, #dda20a 100%)',
                        success: 'linear-gradient(135deg, #1cc88a 0%, #13855c 100%)',
                        danger: 'linear-gradient(135deg, #e74a3b 0%, #be2617 100%)'
                      };
                      
                      const backgroundColors = {
                        primary: 'linear-gradient(135deg, #5a7ec7 0%, #3d5a99 100%)',
                        warning: 'linear-gradient(135deg, #f4d786 0%, #e8bd5c 100%)',
                        success: 'linear-gradient(135deg, #5dd4a8 0%, #3fa67c 100%)',
                        danger: 'linear-gradient(135deg, #ed7465 0%, #d04a3b 100%)'
                      };
                      
                      return (
                        <button
                          key={filter.key}
                          className="btn btn-sm d-flex align-items-center text-white"
                          style={{
                            background: quickFilter === filter.key ? gradients[filter.color] : backgroundColors[filter.color],
                            border: 'none',
                            height: '32px',
                            whiteSpace: 'nowrap',
                            opacity: quickFilter === filter.key ? 1 : 0.85,
                            fontWeight: quickFilter === filter.key ? '600' : '500'
                          }}
                          onClick={() => setQuickFilter(filter.key)}
                        >
                          <Icon className="me-1" />
                          {filter.label}
                        </button>
                      );
                    })}
                    
                    {/* Separator */}
                    <div className="vr mx-1" style={{ height: '30px' }}></div>
                    
                    {/* Action Buttons */}
                    <button 
                      className={`btn btn-sm d-flex align-items-center ${showFilters ? 'text-white' : 'btn-outline-secondary'}`}
                      style={{ 
                        background: showFilters ? 'linear-gradient(135deg, #5a6268 0%, #3d4248 100%)' : 'transparent',
                        border: showFilters ? 'none' : '1px solid #5a6268',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FaFilter className="me-1" />
                      Filters
                    </button>
                    
                    <button 
                      className="btn btn-sm text-white d-flex align-items-center" 
                      style={{ 
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', 
                        border: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={exportToPDF}
                    >
                      <FaFilePdf className="me-1" />
                      PDF
                    </button>
                    
                    <CSVLink
                      data={exportCSVData}
                      filename={`ledger_report_${fmt(new Date(), "ddMMyyyy")}.csv`}
                      className="btn btn-sm text-white d-flex align-items-center"
                      style={{ 
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
                        border: 'none', 
                        textDecoration: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <FaFileCsv className="me-1" />
                      CSV
                    </CSVLink>
                    
                    <button 
                      className="btn btn-sm text-white d-flex align-items-center" 
                      style={{ 
                        background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)', 
                        border: 'none',
                        height: '32px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={resetFilters}
                    >
                      <FaRedo className="me-1" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h6 className="mb-0">
                    <FaFilter className="me-2" />
                    Advanced Filters
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaCalendarAlt className="me-1" />
                        From Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formdata.from}
                        onChange={(e) => setFormData({ ...formdata, from: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaCalendarAlt className="me-1" />
                        To Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formdata.to}
                        onChange={(e) => setFormData({ ...formdata, to: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaTag className="me-1" />
                        Account Type
                      </label>
                      <select
                        className="form-control form-control-sm"
                        value={formdata.accounttype}
                        onChange={(e) => setFormData({ ...formdata, accounttype: e.target.value })}
                        disabled
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                      >
                        {accountTypes.map((type, idx) => (
                          <option key={idx} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-3">
                      <label className="form-label">
                        <FaUser className="me-1" />
                        Customer
                      </label>
                      <select
                        className="form-control form-control-sm"
                        value={formdata.account_id}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedCustomer = customerdata.find(c => c.id === parseInt(selectedId));
                          setFormData({
                            ...formdata,
                            customername: selectedCustomer?.name || "",
                            account_id: selectedId,
                          });
                        }}
                      >
                        <option value="">All Customers</option>
                        {customerdata.map((cust, idx) => (
                          <option key={idx} value={cust.id}>
                            {cust.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading ledger data...</p>
                  </div>
                ) : data.length === 0 ? (
                  <div className="text-center py-5">
                    <FaExclamationCircle size={48} className="text-muted mb-3" />
                    <p className="text-muted">No records found matching your criteria</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>Txn ID</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>A/C Type</th>
                          <th>A/C ID</th>
                          <th>Description</th>
                          <th className="text-end">Debit</th>
                          <th className="text-end">Credit</th>
                          <th className="text-end">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((row, index) => {
                          let runningBalance = 0;
                          data.slice(0, index + 1).forEach(r => {
                            runningBalance += (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
                          });
                          
                          return (
                            <tr key={row.id || index}>
                              <td>
                                <span className="fw-semibold text-primary">{row.transaction_id}</span>
                              </td>
                              <td>{formatDisplayDate(row.date)}</td>
                              <td>{getStatusBadge(row)}</td>
                              <td>
                                <span className="badge bg-light text-dark">{row.account_type}</span>
                              </td>
                              <td>{row.account_id}</td>
                              <td>
                                <div className="text-truncate" style={{ maxWidth: '200px' }} title={row.description}>
                                  {row.description}
                                </div>
                              </td>
                              <td className="text-end">
                                {row.debit_amount ? (
                                  <span className="text-danger fw-semibold">
                                    ₹{Number(row.debit_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="text-end">
                                {row.credit_amount ? (
                                  <span className="text-success fw-semibold">
                                    ₹{Number(row.credit_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="text-end">
                                <span className={`fw-semibold ${runningBalance >= 0 ? 'text-primary' : 'text-warning'}`}>
                                  ₹{runningBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
